import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { iconForMessage } from '@/components/SystemMessages/systemMessageIcons';
import { AnnouncementBubbleState } from './AnnouncementsContext';
import { OPEN_ANNOUNCEMENT_EVENT } from './openAnnouncement';
import styles from './AnnouncementTour.module.scss';

/**
 * One step of a feature announcement's bubble, beside the element it explains (HAT-1489). Rendered by
 * the element's own AnnouncementAnchor - see AnnouncementBubbleState for why.
 */
const AnnouncementBubble = ({ bubble }: { bubble: AnnouncementBubbleState }) => {
  const { t } = useTranslation();
  const { message, stepIndex, element, nextHere, advance, finish, snooze } = bubble;
  const step = message.tour[stepIndex];
  const total = message.tour.length;
  // «הבא» only where it leads somewhere on this screen; otherwise this step is the bubble's last.
  const hasNext = stepIndex < total - 1 && nextHere;
  const title = step.title || (stepIndex === 0 ? t('announcements.newTitle', { title: message.title }) : message.title);
  // The rest of the message is worth a way in when it says more than the bubble does.
  const hasMore = Boolean(message.body) || message.blocks.length > 0;

  return (
    <Popover.Root open>
      <Popover.Anchor virtualRef={{ current: element }} />
      <Popover.Portal>
        <Popover.Content
          className={styles.bubble}
          data-announcement-bubble=""
          side="bottom"
          align="start"
          sideOffset={10}
          collisionPadding={16}
          role="status"
          aria-live="polite"
          // The bubble explains, it does not take over: focus stays where the user put it, and a
          // click elsewhere does not dismiss it - only an answer does.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={snooze}
        >
          <span className={styles.accent} aria-hidden="true" />
          <span className={styles.icon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" focusable="false">
              {iconForMessage(message)}
            </svg>
          </span>
          <div className={styles.body}>
            <span className={`${styles.title} body-md-semibold`}>{title}</span>
            <p className={`${styles.text} body-sm-regular`}>{step.text}</p>
            <div className={styles.actions}>
              <button type="button" className={`${styles.primary} body-sm-semibold`} onClick={hasNext ? advance : finish}>
                {hasNext ? t('announcements.next') : t('announcements.gotIt')}
              </button>
              {hasNext ? (
                <button type="button" className={`${styles.secondary} body-sm-regular`} onClick={finish}>
                  {t('announcements.skip')}
                </button>
              ) : (
                hasMore && (
                  <button
                    type="button"
                    className={`${styles.secondary} body-sm-regular`}
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent(OPEN_ANNOUNCEMENT_EVENT, { detail: { id: message.id } }));
                      finish();
                    }}
                  >
                    {t('announcements.details')}
                  </button>
                )
              )}
              {total > 1 && (hasNext || stepIndex > 0) && <span className={`${styles.step} body-xs-regular`}>{t('announcements.step', { current: stepIndex + 1, total })}</span>}
            </div>
          </div>
          <Popover.Arrow width={16} height={8} asChild>
            <svg className={styles.arrow} viewBox="0 0 16 8" aria-hidden="true">
              <path d="M0 0 L8 8 L16 0" />
            </svg>
          </Popover.Arrow>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default AnnouncementBubble;
